import { type FC, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingSpinner,
  Popover,
  TabDialogComponent,
  TabItem,
  TriggerButton,
  TriggerButtonIntent,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { ActivityTypeEnum, NotificationTypeEnum } from "@/graphql.js";
import {
  type INotificationsState,
  loadNotificationsList,
  markAllAsRead,
  unwatchAllInfoChannels,
  watchAllInfoChannels,
} from "@/redux/modules/notifications.tsx";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";

import { FromNotification } from "../../../modules/notifications/Notification.js";
import style from "./styles/NotificationDialog.module.scss";

// all possible tab labels
enum TAB_OPTIONS {
  ALL = "all",
  COMMENTS = "comments",
  EVENTS = "events",
}

const Loading = () => (
  <div style={{ padding: "20px 10px 10px" }}>
    <LoadingSpinner />
  </div>
);

const NoNotifications = () => (
  <div style={{ padding: "20px 10px 10px", textAlign: "center", width: "100%" }}>You have no notifications.</div>
);

const NotificationDropDown: FC = () => {
  const connected = useSelector<IAppState>((state) => state.websocket.connected);
  const unread = useSelector<IAppState, string | undefined>((state) => {
    const count = state.notifications.notifications.filter((n) => !n.read).length;
    if (count === 0) return;
    return count > 99 ? "99+" : count.toString();
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({
    onOpenChange(isOpen) {
      if (!isOpen && connected) {
        dispatch(markAllAsRead());
      }
    },
  });

  const dispatch = useDispatch<ReduxDispatch>();
  const { notifications, isLoading } = useSelector<IAppState, INotificationsState>((state) => state.notifications);

  const commentsNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => n.type === NotificationTypeEnum.ProjectActivity && n.activity.type === ActivityTypeEnum.Comment,
      ),
    [notifications],
  );

  const eventNotifications = useMemo(
    () =>
      notifications.filter(
        (n) =>
          n.type === NotificationTypeEnum.Job ||
          n.type === NotificationTypeEnum.Workflow ||
          n.type === NotificationTypeEnum.Group,
      ),
    [notifications],
  );

  useEffect(() => {
    void dispatch(loadNotificationsList());
    dispatch(watchAllInfoChannels());

    return () => {
      dispatch(unwatchAllInfoChannels());
    };
  }, []);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        icon={connected ? "notifications" : "notifications_off"}
        label={"Notifications"}
        ref={triggerRef}
        badge={connected ? unread : undefined}
        intent={state.isOpen ? TriggerButtonIntent.Active : undefined}
      />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef} offset={5}>
        <TabDialogComponent closeFn={() => state.close()} width={400}>
          <TabItem key={TAB_OPTIONS.ALL} title={"All Notifications"} icon={"notifications"}>
            {isLoading ? (
              <Loading />
            ) : notifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {notifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
          <TabItem key={TAB_OPTIONS.COMMENTS} title={"Comments"} icon={"chat_bubble"}>
            {isLoading ? (
              <Loading />
            ) : commentsNotifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {commentsNotifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
          <TabItem key={TAB_OPTIONS.EVENTS} title={"Events"} icon={"sync_alt"}>
            {isLoading ? (
              <Loading />
            ) : eventNotifications.length === 0 ? (
              <NoNotifications />
            ) : (
              <VStack className={style.list} spacing={7} style={{ paddingTop: 20 }}>
                {eventNotifications.map((notification) => (
                  <FromNotification key={notification.id} notification={notification} />
                ))}
              </VStack>
            )}
          </TabItem>
        </TabDialogComponent>
      </Popover>
    </>
  );
};

export { NotificationDropDown };
